<template>
  <!-- Main container -->
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <!-- Loading state -->
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <a-spin />
    </div>
    
    <!-- Main content area -->
    <div class="a-body p-20-px flex-grow-1 d-flex flex-column overflow-y-auto">
      <transition name="slide-up">
        <div v-if="!isLoading" class="d-flex flex-column flex-grow-1">
          <a-divider>Impersonation Permissions</a-divider>
            
          <!-- Search and action buttons -->
          <div class="search-container">
            <div class="custom-search-wrapper">
              <a-input
                placeholder="Search current RBAC users"
                v-model="searchQuery"
                class="custom-search-input"
                allowClear
                @input="onSearchInput"
              />
              <a-button type="primary" class="custom-search-button" @click="searchUsers">
                <a-icon type="search" />
              </a-button>
            </div>
            <div class="button-group">
              <a-button type="danger" class="mr-2" v-if="selectedRbacUserIds.length > 0" @click="confirmBatchRemovePermissions">
                <a-icon type="delete" /> Delete Selected ({{ selectedRbacUserIds.length }})
              </a-button>
              <a-button type="primary" @click="showAddUserModal">
                <a-icon type="plus" /> Add User
              </a-button>
              <a-button class="ml-2" icon="copy" @click="copyRbacVisible = true">
                Copy from user
              </a-button>
            </div>
          </div>
            
          <!-- RBAC permissions table -->
          <div class="table-container flex-grow-1">
            <a-table 
              :columns="rbacColumns" 
              :dataSource="paginatedPermissions" 
              :pagination="false"
              :rowKey="record => record.custCustomerId"
              :rowSelection="{
                selectedRowKeys: selectedRbacUserIds,
                onChange: onRbacSelectionChange
              }"
              :scroll="{ y: 500 }"
              size="middle"
              bordered
              class="rbac-table"
              :loading="tableLoading"
            >
              <!-- Table title -->
              <div slot="title">
                <div class="table-title">RBAC Permissions List</div>
              </div>
              
              <!-- Table footer with pagination -->
              <template slot="footer">
                <div class="pagination-container">
                  <a-pagination 
                    :current="paginationConfig.current || 1"
                    :pageSize="paginationConfig.pageSize"
                    :total="filteredPermissions.length"
                    :showTotal="paginationConfig.showTotal"
                    :showSizeChanger="true"
                    :pageSizeOptions="paginationConfig.pageSizeOptions"
                    :showQuickJumper="true"
                    @change="onPaginationChange"
                    @showSizeChange="onShowSizeChange"
                  />
                </div>
              </template>
              
              <!-- Custom cell renderers -->
              <template slot="custUsername" slot-scope="text">
                <span class="user-cell">{{ text }}</span>
              </template>
              <template slot="custCustomerId" slot-scope="text">
                <span class="id-cell">{{ text }}</span>
              </template>
              <template slot="action" slot-scope="text, record">
                <a-button type="danger" size="small" @click="confirmRemovePermission(record)">
                  <a-icon type="delete" /> Remove
                </a-button>
              </template>
              
              <!-- Empty state -->
              <template slot="emptyText">
                <div class="text-center p-3">
                  <p>No RBAC permissions found</p>
                  <a-button type="primary" @click="showAddUserModal">
                    <a-icon type="plus" /> Add User
                  </a-button>
                </div>
              </template>
            </a-table>
          </div>
        </div>
      </transition>
    </div>
    
    <!-- Add User Modal -->
    <a-modal
      title="Add User to RBAC Permissions"
      :visible="addUserModalVisible"
      :confirmLoading="addUserLoading"
      @ok="handleAddUser"
      @cancel="handleAddUserCancel"
      width="800px"
      centered
    >
      <!-- Modal search field -->
      <div class="modal-search-container">
        <div class="custom-search-wrapper">
          <a-input
            placeholder="Search by username or customer ID"
            v-model="modalSearchQuery"
            class="custom-search-input"
            allowClear
            @input="onModalSearchInput"
          />
          <a-button type="primary" class="custom-search-button" @click="searchUsersForModal" :disabled="modalLoading">
            <a-icon v-if="modalLoading" type="loading" />
            <a-icon v-else type="search" />
          </a-button>
        </div>
      </div>
      
      <!-- Modal users table -->
      <div class="modal-table-container">
        <a-table 
          :columns="userColumns" 
          :dataSource="modalLoading ? [] : paginatedAvailableUsers" 
          :pagination="false"
          :rowKey="record => record.customerId"
          :rowSelection="{
            selectedRowKeys: selectedUserIds,
            onChange: onSelectChange
          }"
          :scroll="{ y: 400 }"
          size="middle"
          bordered
          class="rbac-modal-table"
        >
          <!-- Table title -->
          <div slot="title">
            <div class="table-title">
              Available Users
              <a-spin v-if="modalLoading" class="ml-2" size="small" />
            </div>
          </div>
          
          <!-- Custom cell renderers -->
          <template slot="userName" slot-scope="text">
            <span class="user-cell">{{ text }}</span>
          </template>
          <template slot="customerId" slot-scope="text">
            <span class="id-cell">{{ text }}</span>
          </template>
          
          <!-- Table footer with pagination -->
          <template slot="footer">
            <div class="pagination-container">
              <a-pagination 
                :current="modalPaginationConfig.current || 1"
                :pageSize="modalPaginationConfig.pageSize"
                :total="availableUsers.length"
                :showTotal="modalPaginationConfig.showTotal"
                :showSizeChanger="true"
                :pageSizeOptions="modalPaginationConfig.pageSizeOptions"
                @change="onModalPaginationChange"
                @showSizeChange="onModalShowSizeChange"
              />
            </div>
          </template>
          
          <!-- Empty state -->
          <template slot="emptyText">
            <div class="empty-content">
              <div v-if="modalLoading" class="loading-container">
                <a-spin size="large" />
              </div>
              <div v-else class="text-center p-3">
                <p>No users found matching your search criteria</p>
              </div>
            </div>
          </template>
        </a-table>
      </div>
      
      <!-- Selection indicator -->
      <div class="modal-footer-info" v-if="selectedUserIds.length > 0">
        <a-tag color="blue">{{ selectedUserIds.length }} user(s) selected</a-tag>
      </div>
    </a-modal>

    <CopyRbacModal
      :visible="copyRbacVisible"
      :targets="[copyRbacTarget]"
      @close="copyRbacVisible = false"
      @done="onCopyRbacDone"
    />
  </div>
</template>

<script>
/**
 * RbacTab Component
 * 
 * This component allows administrators to manage RBAC permissions for a specific user.
 * It provides functionality to view, add, and remove impersonation permissions.
 */

import UserManagementService from '../../services/UserManagement.service';
import CopyRbacModal from '../CopyRbacModal.vue';

export default {
  name: 'RbacTab',
  components: { CopyRbacModal },
  props: {
    user: { type: Object, required: true }
  },
  data() {
    return {
      isLoading: true,
      tableLoading: false,
      rbacPermissions: [],
      searchQuery: '',
      addUserModalVisible: false,
      addUserLoading: false,
      modalSearchQuery: '',
      modalLoading: false,
      availableUsers: [],
      selectedUserIds: [],
      selectedRbacUserIds: [],
      copyRbacVisible: false,
      rbacColumns: [
        {
          title: 'Customer ID',
          dataIndex: 'custCustomerId',
          key: 'custCustomerId',
          width: '20%',
          scopedSlots: { customRender: 'custCustomerId' },
          sorter: (a, b) => {
            const idA = a.custCustomerId ? Number(a.custCustomerId) : 0;
            const idB = b.custCustomerId ? Number(b.custCustomerId) : 0;
            return idA - idB;
          }
        },
        {
          title: 'Username',
          dataIndex: 'custUsername',
          key: 'custUsername',
          width: '60%',
          scopedSlots: { customRender: 'custUsername' },
          sorter: (a, b) => {
            const nameA = a.custUsername || '';
            const nameB = b.custUsername || '';
            return nameA.localeCompare(nameB);
          }
        },
        {
          title: 'Action',
          key: 'action',
          width: '20%',
          scopedSlots: { customRender: 'action' }
        }
      ],
      userColumns: [
        {
          title: 'Customer ID',
          dataIndex: 'customerId',
          key: 'customerId',
          width: '30%',
          scopedSlots: { customRender: 'customerId' },
          sorter: (a, b) => {
            const idA = a.customerId ? Number(a.customerId) : 0;
            const idB = b.customerId ? Number(b.customerId) : 0;
            return idA - idB;
          }
        },
        {
          title: 'Username',
          dataIndex: 'userName',
          key: 'userName',
          width: '70%',
          scopedSlots: { customRender: 'userName' },
          sorter: (a, b) => {
            const nameA = a.userName || '';
            const nameB = b.userName || '';
            return nameA.localeCompare(nameB);
          }
        }
      ],
      filteredPermissions: [],
      allAvailableUsers: [],
      current: 1,
      pageSize: 10,
      modalCurrent: 1,
      modalPageSize: 10,
      searchTimeout: null
    };
  },
  computed: {
    /**
     * Configuration for the main table pagination
     */
    paginationConfig() {
      return {
        current: this.current,
        pageSize: this.pageSize,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100', '200'],
        showTotal: (total) => `Total ${total} records found.`,
        size: 'default',
        showQuickJumper: true,
        position: 'bottomRight'
      };
    },
    
    /**
     * Configuration for the modal table pagination
     */
    modalPaginationConfig() {
      return {
        current: this.modalCurrent,
        pageSize: this.modalPageSize,
        showSizeChanger: true,
        pageSizeOptions: ['5', '10', '20', '50'],
        showTotal: (total) => `Total ${total} records found.`,
        size: 'default',
        showQuickJumper: true,
        position: 'bottomRight'
      };
    },
    
    /**
     * Returns the paginated list of RBAC permissions for the current page
     */
    paginatedPermissions() {
      const startIndex = (this.current - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredPermissions.slice(startIndex, endIndex);
    },
    
    /**
     * Returns the paginated list of available users for the current page in the modal
     */
    paginatedAvailableUsers() {
      const startIndex = (this.modalCurrent - 1) * this.modalPageSize;
      const endIndex = startIndex + this.modalPageSize;
      return this.availableUsers.slice(startIndex, endIndex);
    },

    /**
     * Copy target built from the loaded grants; the modal prefers the grants in its own fresh user list
     */
    copyRbacTarget() {
      return {
        customerId: this.user.customerId,
        userName: this.user.userName,
        roleBasedAccessControl: this.rbacPermissions.map(perm => perm.custCustomerId)
      };
    }
  },
  async created() {
    // ---> Vue lifecycle > [created] > Load RBAC permissions
    await this.fetchRbacPermissions();
    this.isLoading = false;
  },
  methods: {
    /**
     * Fetch RBAC permissions for the current user
     * ---> created/UI interactions > [fetchRbacPermissions] > UserManagementService.getRbacPermissions
     */
    async fetchRbacPermissions() {
      try {
        this.tableLoading = true;
        const rbacResponse = await UserManagementService.getRbacPermissions(this.user.customerId);
        this.rbacPermissions = rbacResponse.rbac_permissions || [];
        this.filteredPermissions = [...this.rbacPermissions];
      } catch (error) {
        this.$showErrorMessage('Error loading RBAC permissions');
      } finally {
        this.tableLoading = false;
      }
    },
    
    /**
     * Handle search input with debounce
     * ---> User input > [onSearchInput] > searchUsers
     */
    onSearchInput() {
      // Trigger search after a short delay to avoid too many requests
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(() => {
        this.searchUsers();
      }, 300);
    },
    
    /**
     * Handle modal search input with debounce
     * ---> User input > [onModalSearchInput] > searchUsersForModal
     */
    onModalSearchInput() {
      // Trigger search after a short delay to avoid too many requests
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(() => {
        this.searchUsersForModal();
      }, 300);
    },
    
    /**
     * Filter RBAC permissions based on search query
     * ---> onSearchInput > [searchUsers] > Filter permissions locally
     */
    searchUsers() {
      // Filter the RBAC permissions based on the search query
      if (!this.searchQuery) {
        this.filteredPermissions = [...this.rbacPermissions];
        return;
      }
      
      const query = this.searchQuery.toLowerCase();
      this.tableLoading = true;
      
      // Filter locally from already loaded permissions
      this.filteredPermissions = this.rbacPermissions.filter(
        perm => 
          (perm.custUsername && perm.custUsername.toLowerCase().includes(query)) || 
          (perm.custCustomerId && perm.custCustomerId.toString().includes(query))
      );
      
      this.tableLoading = false;
    },
    
    /**
     * Show the add user modal and load available users
     * ---> UI button click > [showAddUserModal] > searchUsersForModal
     */
    async showAddUserModal() {
      this.addUserModalVisible = true;
      this.selectedUserIds = [];
      this.modalSearchQuery = '';
      this.modalCurrent = 1;
      
      // Reset available users to force a fresh reload
      this.allAvailableUsers = [];
      this.availableUsers = [];
      
      await this.searchUsersForModal();
    },
    
    /**
     * Search for users to add to RBAC permissions
     * ---> showAddUserModal/onModalSearchInput > [searchUsersForModal] > UserManagementService.fetchListUsers
     */
    async searchUsersForModal() {
      try {
        this.modalLoading = true;
        
        // Always fetch fresh data
        // Get all users except those already in RBAC permissions
        const allUsers = await UserManagementService.fetchListUsers();
        const existingIds = this.rbacPermissions.map(perm => perm.custCustomerId);
        
        // Check if the current user should be excluded
        const hasSelfRbacPermission = existingIds.includes(Number(this.user.customerId));
        
        this.allAvailableUsers = allUsers.filter(user => {
          // Don't include users that already have RBAC permissions
          if (existingIds.includes(user.customerId)) {
            return false;
          }
          
          // If it's the current user, only include if they don't have self-RBAC permission
          if (user.customerId === Number(this.user.customerId)) {
            return !hasSelfRbacPermission;
          }
          
          // Include all other users
          return true;
        });
        
        // Convert search query to lowercase for case-insensitive comparison
        const searchTerm = this.modalSearchQuery.toLowerCase();
        
        this.availableUsers = !searchTerm 
          ? [...this.allAvailableUsers] 
          : this.allAvailableUsers.filter(user => 
              (user.userName && user.userName.toLowerCase().includes(searchTerm)) ||
              (user.customerId && user.customerId.toString().includes(searchTerm)));
      } catch (error) {
        this.$showErrorMessage('Error searching for users');
      } finally {
        this.modalLoading = false;
      }
    },
    
    /**
     * Close the copy modal and reload the grants unless the session expired
     * ---> CopyRbacModal done > [onCopyRbacDone] > fetchRbacPermissions
     */
    async onCopyRbacDone({ stoppedBy }) {
      this.copyRbacVisible = false;
      if (stoppedBy === 401) {
        return;
      }
      this.selectedRbacUserIds = [];
      await this.fetchRbacPermissions();
    },
    
    /**
     * Handle selection change in the modal table
     * ---> UI selection change > [onSelectChange] > Update selectedUserIds
     */
    onSelectChange(selectedRowKeys) {
      this.selectedUserIds = selectedRowKeys;
    },
    
    /**
     * Add selected users to RBAC permissions
     * ---> Modal confirmation > [handleAddUser] > UserManagementService.batchAddRbacPermissions > fetchRbacPermissions
     */
    async handleAddUser() {
      if (!this.selectedUserIds.length) {
        this.$message.warning('Please select at least one user');
        return;
      }
      
      try {
        this.addUserLoading = true;
        
        // Add batch RBAC permissions and handle updated response
        const response = await UserManagementService.batchAddRbacPermissions(
          this.user.customerId, 
          this.selectedUserIds
        );
        
        // Handle different response messages based on status
        const successMessage = response.message || 'RBAC permissions added successfully';
        this.$message.success(successMessage);
        
        this.addUserModalVisible = false;
        
        // Reset modal state
        this.selectedUserIds = [];
        this.modalSearchQuery = '';
        this.modalCurrent = 1;
        this.modalPageSize = 10;
        
        // Clear cached data
        this.allAvailableUsers = [];
        this.availableUsers = [];
        
        // Reload RBAC permissions
        await this.fetchRbacPermissions();
      } catch (error) {
        // Handle specific error messages from API
        const errorMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Error adding RBAC permissions';
        
        this.$showErrorMessage(errorMessage);
      } finally {
        this.addUserLoading = false;
      }
    },
    
    /**
     * Cancel adding users and reset modal state
     * ---> Modal cancel > [handleAddUserCancel] > Reset modal state
     */
    handleAddUserCancel() {
      this.addUserModalVisible = false;
      
      // Reset modal state for next time
      this.selectedUserIds = [];
      this.modalSearchQuery = '';
      this.modalCurrent = 1;
      this.modalPageSize = 10;
      
      // Clear cached data
      this.allAvailableUsers = [];
      this.availableUsers = [];
    },
    
    /**
     * Remove a specific RBAC permission
     * ---> confirmRemovePermission > [removePermission] > UserManagementService.removeRbacPermission
     */
    async removePermission(record) {
      try {
        this.tableLoading = true;
        
        // Remove RBAC permission
        await UserManagementService.removeRbacPermission(
          this.user.customerId, 
          record.custCustomerId
        );
        
        // Status 204 No Content is expected for success
        this.$message.success('RBAC permission removed successfully');
        
        // Update local lists immediately
        this.rbacPermissions = this.rbacPermissions.filter(
          perm => perm.custCustomerId !== record.custCustomerId
        );
        this.filteredPermissions = this.filteredPermissions.filter(
          perm => perm.custCustomerId !== record.custCustomerId
        );
        
      } catch (error) {
        console.error('Error removing RBAC permission:', error);
        
        // Show more specific error message if available from updated API response
        const errorMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Error removing RBAC permission';
          
        this.$showErrorMessage(errorMessage);
      } finally {
        this.tableLoading = false;
      }
    },
    
    /**
     * Show confirmation dialog before removing permission
     * ---> UI button click > [confirmRemovePermission] > removePermission
     */
    confirmRemovePermission(record) {
      this.$confirm({
        title: 'Are you sure you want to remove this permission?',
        content: `This will remove impersonation permission for ${record.custUsername || record.custCustomerId}`,
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: () => {
          this.removePermission(record);
        }
      });
    },
    
    /**
     * Handle pagination change for main table
     * ---> Pagination UI interaction > [onPaginationChange] > Update current page
     */
    onPaginationChange(page) {
      this.current = page;
    },
    
    /**
     * Handle page size change for main table
     * ---> UI interaction > [onShowSizeChange] > Update page size and reset to first page
     */
    onShowSizeChange(current, size) {
      this.pageSize = size;
      this.current = 1; // Reset to first page when changing page size
    },
    
    /**
     * Handle selection change in the main RBAC table
     * ---> UI selection > [onRbacSelectionChange] > Update selectedRbacUserIds
     */
    onRbacSelectionChange(selectedRowKeys) {
      this.selectedRbacUserIds = selectedRowKeys;
    },
    
    /**
     * Show confirmation dialog before batch removing permissions
     * ---> UI button click > [confirmBatchRemovePermissions] > batchRemovePermissions
     */
    confirmBatchRemovePermissions() {
      if (!this.selectedRbacUserIds.length) {
        this.$message.warning('Please select at least one user');
        return;
      }
      
      this.$confirm({
        title: 'Are you sure you want to remove these permissions?',
        content: `This will remove impersonation permissions for ${this.selectedRbacUserIds.length} user(s)`,
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: () => {
          this.batchRemovePermissions();
        }
      });
    },
    
    /**
     * Remove multiple RBAC permissions at once
     * ---> confirmBatchRemovePermissions > [batchRemovePermissions] > UserManagementService.batchRemoveRbacPermissions
     */
    async batchRemovePermissions() {
      try {
        this.tableLoading = true;
        
        // Remove RBAC permissions in batch
        const response = await UserManagementService.batchRemoveRbacPermissions(
          this.user.customerId, 
          this.selectedRbacUserIds
        );
        
        // Check for updated response format (204 No Content doesn't return body)
        const successMessage = response && response.message 
          ? response.message 
          : `${this.selectedRbacUserIds.length} RBAC permissions removed successfully`;
        
        this.$message.success(successMessage);
        
        // Update local lists immediately
        this.rbacPermissions = this.rbacPermissions.filter(
          perm => !this.selectedRbacUserIds.includes(perm.custCustomerId)
        );
        this.filteredPermissions = this.filteredPermissions.filter(
          perm => !this.selectedRbacUserIds.includes(perm.custCustomerId)
        );
        
        // Clear selection
        this.selectedRbacUserIds = [];
      } catch (error) {
        console.error('Error removing RBAC permissions:', error);
        
        // Show more specific error message if available from updated response
        const errorMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Error removing RBAC permissions';
          
        this.$showErrorMessage(errorMessage);
      } finally {
        this.tableLoading = false;
      }
    },
    
    /**
     * Handle pagination change for modal table
     * ---> Modal pagination UI interaction > [onModalPaginationChange] > Update modal current page
     */
    onModalPaginationChange(page) {
      this.modalCurrent = page;
    },
    
    /**
     * Handle page size change for modal table
     * ---> Modal UI interaction > [onModalShowSizeChange] > Update modal page size and reset to first page
     */
    onModalShowSizeChange(current, size) {
      this.modalPageSize = size;
      this.modalCurrent = 1; // Reset to first page when changing page size
    },
    
    /**
     * Handle search change (used for clearing)
     * ---> Search input cleared > [onSearchChange] > Reset filtered permissions
     */
    onSearchChange(e) {
      // Handle search clearing
      if (!e.target.value) {
        this.filteredPermissions = [...this.rbacPermissions];
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* User name display styling */
.user-name {
  word-break: break-all;
  margin: 0;
}

/* Search container with flex layout */
.search-container {
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .custom-search-wrapper {
    width: 400px;
    display: flex;
    
    .custom-search-input {
      flex: 1;
      height: 36px;
      font-size: 14px;
      border-radius: 4px;
    }
    
    .custom-search-button {
      height: 36px;
      margin-left: 8px;
      border-radius: 4px;
    }
  }
  
  .button-group {
    display: flex;
    align-items: center;
  }
}

/* Table container with flex layout for proper scrolling */
.table-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 500px;
  margin-bottom: 16px;
  
  .ant-table-wrapper {
    background-color: #fff;
    border-radius: 4px;
  }
}

/* Cell styling for better readability */
.user-cell {
  font-weight: 500;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.id-cell {
  font-family: monospace;
  font-weight: 500;
}

/* Table wrapper flex layout */
.ant-table-wrapper {
  flex: 1;
  
  .ant-spin-nested-loading, 
  .ant-spin-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .ant-table {
    flex: 1;
    overflow: auto;
    
    .ant-table-thead > tr > th {
      background-color: #f7f7f7;
      font-weight: 600;
      white-space: nowrap;
      border-bottom: 1px solid #e8e8e8;
      box-sizing: border-box;
    }
    
    .ant-pagination {
      display: flex;
      justify-content: flex-end;
      margin: 16px 0;
    }
  }
}

/* Enhanced table header styling */
.rbac-table ::v-deep .ant-table-thead > tr > th {
  background-color: #f7f7f7;
  font-weight: 600;
  white-space: nowrap;
  overflow: visible;
  border-bottom: 1px solid #e8e8e8;
  box-sizing: border-box;
}

/* Pagination styling */
.rbac-table ::v-deep .ant-pagination {
  display: flex;
  justify-content: flex-end;
  margin: 16px 0;
}

.rbac-table ::v-deep .ant-select {
  margin-left: 8px;
}

.rbac-table ::v-deep .ant-pagination-options {
  margin-left: 16px;
}

/* Modal search container */
.modal-search-container {
  margin-bottom: 16px;
  
  .custom-search-wrapper {
    width: 100%;
    display: flex;
    
    .custom-search-input {
      flex: 1;
      height: 36px;
      font-size: 14px;
      border-radius: 4px;
    }
    
    .custom-search-button {
      height: 36px;
      margin-left: 8px;
      border-radius: 4px;
    }
  }
}

/* Modal footer with selection info */
.modal-footer-info {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

/* Table title styling */
.table-title {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

/* Pagination container positioning */
.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

/* Fix for table header styling to prevent overflow issues */
.rbac-table ::v-deep .ant-table-thead > tr > th {
  background-color: #f7f7f7;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid #e8e8e8 !important;
  box-sizing: border-box;
  padding: 12px 8px;
  position: relative;
  overflow: hidden !important;
}

/* Action column styling */
.rbac-table ::v-deep .ant-table-thead > tr > th:last-child {
  overflow: visible !important;
  padding-right: 16px !important;
}

/* Pagination options display fix */
.rbac-table ::v-deep .ant-pagination-options {
  display: inline-block !important;
}

.rbac-table ::v-deep .ant-pagination-options-size-changer.ant-select {
  display: inline-block !important;
  margin-right: 8px;
}

/* Modal loading container */
.modal-loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
}

/* Search input focus effect */
.custom-search-input:focus {
  border-color: #40a9ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* Modal table container */
.modal-table-container {
  margin-bottom: 16px;
  min-height: 400px;
  
  .ant-table-wrapper {
    background-color: #fff;
    border-radius: 4px;
  }
}

/* Empty content placeholder styling */
.empty-content {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

/* Loading spinner container */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 400px;
}

/* Modal table styling enhancements */
.rbac-modal-table ::v-deep .ant-table-thead > tr > th {
  background-color: #f7f7f7;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid #e8e8e8 !important;
  box-sizing: border-box;
  padding: 12px 8px;
  position: relative;
  overflow: hidden !important;
}

.rbac-modal-table ::v-deep .ant-pagination {
  display: flex;
  justify-content: flex-end;
  margin: 16px 0;
}

.rbac-modal-table ::v-deep .ant-select {
  margin-left: 8px;
}

.rbac-modal-table ::v-deep .ant-pagination-options {
  margin-left: 16px;
}

/* Fix for spinner positioning in modal */
.rbac-modal-table ::v-deep .ant-spin-nested-loading {
  width: 100%;
  height: 100%;
}

.rbac-modal-table ::v-deep .ant-spin-container {
  height: 100%;
}

.rbac-modal-table ::v-deep .ant-spin {
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: none;
}

.rbac-modal-table ::v-deep .ant-spin-spinning {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Modal search loading indicator */
.modal-search-loading {
  display: flex;
  align-items: center;
  margin-top: 10px;
  color: #1890ff;
  
  .loading-text {
    margin-left: 8px;
    font-size: 14px;
  }
}
</style> 